import React, { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Loader2, Plus, X } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { useTranslation } from "react-i18next";
import { MmRegister, OPERATOR_META } from "../../api/mentalModels";
import { useMmText } from "../../hooks/useMmText";

const FAMILY_ACCENTS: Record<string, string> = {
  create: "bg-sky-400",
  form: "bg-amber-400",
  relations: "bg-violet-400",
  epistemic: "bg-emerald-400",
  affect: "bg-rose-400",
  design: "bg-indigo-400",
};

const FAMILY_ORDER = ["create", "form", "relations", "epistemic", "affect", "design"];

interface Props {
  register: MmRegister;
  initialName?: string;
  initialSteps?: string[];
  busy?: boolean;
  onSave: (name: string, steps: string[]) => void;
  onCancel: () => void;
}

/**
 * Small path builder: name + ordered operator steps with add/remove/reorder.
 * Used from "save as path" (prefilled with the current route) and from the
 * path picker's "create your own path".
 */
const MmPathEditor: React.FC<Props> = ({
  register,
  initialName = "",
  initialSteps = [],
  busy,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { tm } = useMmText(register);
  const [name, setName] = useState(initialName);
  const [steps, setSteps] = useState<string[]>(initialSteps);

  const available = useMemo(
    () =>
      Object.keys(OPERATOR_META)
        .filter((id) => OPERATOR_META[id].interaction !== "capture" && !steps.includes(id))
        .sort(
          (a, b) =>
            FAMILY_ORDER.indexOf(OPERATOR_META[a].family) -
            FAMILY_ORDER.indexOf(OPERATOR_META[b].family)
        ),
    [steps]
  );

  const move = (index: number, delta: number) => {
    const next = [...steps];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setSteps(next);
  };

  return (
    <div className="rounded-xl border border-primary/25 bg-primary/[0.03] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">{tm("path_editor_title", "Build your path")}</h4>
        <button
          onClick={onCancel}
          className="text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors duration-150 px-2 py-1 -me-2"
        >
          {t("cancel", "Cancel")}
        </button>
      </div>

      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={tm("template_name_placeholder", "Path name…")}
        className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        autoFocus
      />

      {steps.length > 0 ? (
        <div className="space-y-1.5">
          {steps.map((id, i) => (
            <div
              key={id}
              className="flex items-center gap-2 rounded-lg border border-border/70 bg-white px-2.5 py-1.5"
            >
              <span className="flex items-center justify-center h-5 w-5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold tabular-nums flex-shrink-0">
                {i + 1}
              </span>
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full flex-shrink-0",
                  FAMILY_ACCENTS[OPERATOR_META[id]?.family] || "bg-slate-300"
                )}
              />
              <span className="flex-1 text-sm truncate">{tm(`op_${id}_title`, id)}</span>
              <button
                onClick={() => move(i, -1)}
                disabled={i === 0}
                aria-label={tm("move_up", "Move up")}
                className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 cursor-pointer transition-colors duration-150"
              >
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => move(i, 1)}
                disabled={i === steps.length - 1}
                aria-label={tm("move_down", "Move down")}
                className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 cursor-pointer transition-colors duration-150"
              >
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setSteps(steps.filter((s) => s !== id))}
                aria-label={t("remove", "Remove")}
                className="p-1 text-muted-foreground hover:text-rose-600 cursor-pointer transition-colors duration-150"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {tm("path_editor_empty", "Add steps below — the order is the path.")}
        </p>
      )}

      <div>
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">
          {tm("path_editor_add", "Add a step")}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {available.map((id) => (
            <button
              key={id}
              onClick={() => steps.length < 8 && setSteps([...steps, id])}
              disabled={steps.length >= 8}
              className={cn(
                "flex items-center gap-1.5 rounded-full border border-border/80 bg-white px-2.5 py-1 text-xs cursor-pointer",
                "transition-all duration-150 hover:border-primary/40 hover:shadow-sm disabled:opacity-40",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              )}
            >
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  FAMILY_ACCENTS[OPERATOR_META[id]?.family] || "bg-slate-300"
                )}
              />
              {tm(`op_${id}_title`, id)}
              <Plus className="w-3 h-3 text-muted-foreground" />
            </button>
          ))}
        </div>
        {steps.length >= 8 && (
          <p className="text-[11px] text-amber-700 mt-1.5">
            {tm("path_editor_max", "A path holds up to 8 steps — remove one to add another.")}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <Button
          size="sm"
          disabled={busy || !name.trim() || steps.length === 0}
          onClick={() => onSave(name.trim(), steps)}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
          {tm("path_editor_save", "Save path")}
        </Button>
      </div>
    </div>
  );
};

export default MmPathEditor;
